import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [consultationFormData, setConsultationFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    notes: '',
  });

  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [consultationSubmitting, setConsultationSubmitting] = useState(false);
  const [consultationSuccess, setConsultationSuccess] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#consultation') {
      setTimeout(() => {
        const element = document.getElementById('consultation');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contactFormData.name || !contactFormData.email || !contactFormData.message) {
      alert('Please fill in all required fields');
      return;
    }

    setContactSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('name', contactFormData.name);
      formData.append('email', contactFormData.email);
      formData.append('phone', contactFormData.phone);
      formData.append('service', contactFormData.service);
      formData.append('message', contactFormData.message);

      const response = await fetch('https://readdy.ai/api/form/d5vpil9r44f5krorlaq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setContactSuccess(true);
        setContactFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
        setTimeout(() => setContactSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consultationFormData.name || !consultationFormData.email || !consultationFormData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    setConsultationSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('name', consultationFormData.name);
      formData.append('email', consultationFormData.email);
      formData.append('phone', consultationFormData.phone);
      formData.append('company', consultationFormData.company);
      formData.append('service', consultationFormData.service);
      formData.append('preferredDate', consultationFormData.preferredDate);
      formData.append('preferredTime', consultationFormData.preferredTime);
      formData.append('notes', consultationFormData.notes);

      const response = await fetch('https://readdy.ai/api/form/d5vpil9r44f5krorlaqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setConsultationSuccess(true);
        setConsultationFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          preferredDate: '',
          preferredTime: '',
          notes: '',
        });
        setTimeout(() => setConsultationSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setConsultationSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-8 bg-gradient-to-b from-white to-[#f7fafc]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#718096] tracking-wider uppercase mb-4">
            GET IN TOUCH
          </p>
          <h1 className="text-5xl font-bold text-[#1a2b4a] mb-6">Contact Us</h1>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Have questions about our security solutions? We're here to help. Reach out to us and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-[#1a2b4a] mb-8">Get in Touch</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#e6f2ff] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-xl text-[#1a2b4a]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1a2b4a] mb-1">Phone Number</h3>
                    <a href="tel:+15551234567" className="text-sm text-gray-600 hover:text-[#1a2b4a] cursor-pointer">(647) 280-0088</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#e6f2ff] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-xl text-[#1a2b4a]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1a2b4a] mb-1">Email Address</h3>
                    <a href="mailto:info@inoffice.ca" className="text-sm text-gray-600 hover:text-[#1a2b4a] cursor-pointer">info@inoffice.ca</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#e6f2ff] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-xl text-[#1a2b4a]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1a2b4a] mb-1">Business Hours</h3>
                    <p className="text-sm text-gray-600">Monday - Friday: 7:00 AM - 6:00 PM<br />Saturday - Sunday: 8:00 AM - 6:00 PM<br />Sunday: Closed</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#f7fafc] rounded-lg p-6">
                <h3 className="text-base font-semibold text-[#1a2b4a] mb-3">Emergency Support</h3>
                <p className="text-sm text-gray-600 mb-3">For urgent security issues, our 24/7 emergency line is always available:</p>
                <a href="tel:+15559876543" className="text-base font-semibold text-[#2563eb] hover:text-[#1a2b4a] cursor-pointer">(647) 280-0088</a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-2xl font-bold text-[#1a2b4a] mb-6">Send Us a Message</h3>
                
                {contactSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6 text-sm">
                    Thank you for contacting us! We'll get back to you soon.
                  </div>
                )}

                <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        value={contactFormData.name}
                        onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        value={contactFormData.email}
                        onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        value={contactFormData.phone}
                        onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-service" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Interest
                      </label>
                      <select
                        id="contact-service"
                        name="service"
                        value={contactFormData.service}
                        onChange={(e) => setContactFormData({ ...contactFormData, service: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent cursor-pointer"
                      >
                        <option value="">Select a service</option>
                        <option value="CCTV Systems">CCTV Systems</option>
                        <option value="Access Control">Access Control</option>
                        <option value="Building Security">Building Security</option>
                        <option value="Network & Security Management">Network &amp; Security Management</option>
                        <option value="Cloud Solutions">Cloud Solutions</option>
                        <option value="IT Infrastructure">IT Infrastructure</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      value={contactFormData.message}
                      onChange={(e) => setContactFormData({ ...contactFormData, message: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent resize-none"
                      required
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{contactFormData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    className="w-full bg-[#1a2b4a] text-white px-8 py-3 rounded-lg text-sm font-medium hover:bg-[#2a3b5a] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {contactSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Booking Section */}
      <section id="consultation" className="py-20 px-6 lg:px-8 bg-[#f7fafc]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-[#718096] tracking-wider uppercase mb-4">
              FREE CONSULTATION
            </p>
            <h2 className="text-4xl font-bold text-[#1a2b4a] mb-4">Schedule Your Consultation</h2>
            <p className="text-base text-gray-600">
              Book a free consultation with our security experts to discuss your specific needs and get a customized solution.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            {consultationSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6 text-sm">
                Your consultation has been scheduled! We'll contact you shortly to confirm the details.
              </div>
            )}

            <form id="consultation-form" data-readdy-form onSubmit={handleConsultationSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="consultation-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="consultation-name"
                    name="name"
                    value={consultationFormData.name}
                    onChange={(e) => setConsultationFormData({ ...consultationFormData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="consultation-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="consultation-email"
                    name="email"
                    value={consultationFormData.email}
                    onChange={(e) => setConsultationFormData({ ...consultationFormData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="consultation-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="consultation-phone"
                    name="phone"
                    value={consultationFormData.phone}
                    onChange={(e) => setConsultationFormData({ ...consultationFormData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="consultation-company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="consultation-company"
                    name="company"
                    value={consultationFormData.company}
                    onChange={(e) => setConsultationFormData({ ...consultationFormData, company: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="consultation-service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type
                </label>
                <select
                  id="consultation-service"
                  name="service"
                  value={consultationFormData.service}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, service: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="CCTV Systems">CCTV Systems</option>
                  <option value="Access Control">Access Control</option>
                  <option value="Building Security">Building Security</option>
                  <option value="Network & Security Management">Network &amp; Security Management</option>
                  <option value="Cloud Solutions">Cloud Solutions</option>
                  <option value="IT Infrastructure">IT Infrastructure</option>
                  <option value="Complete Solution">Complete Solution</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="consultation-date" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    id="consultation-date"
                    name="preferredDate"
                    value={consultationFormData.preferredDate}
                    onChange={(e) => setConsultationFormData({ ...consultationFormData, preferredDate: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent cursor-pointer"
                  />
                </div>
                <div>
                  <label htmlFor="consultation-time" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time
                  </label>
                  <select
                    id="consultation-time"
                    name="preferredTime"
                    value={consultationFormData.preferredTime}
                    onChange={(e) => setConsultationFormData({ ...consultationFormData, preferredTime: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent cursor-pointer"
                  >
                    <option value="">Select a time</option>
                    <option value="Morning (8:00 AM - 12:00 PM)">Morning (8:00 AM - 12:00 PM)</option>
                    <option value="Afternoon (12:00 PM - 4:00 PM)">Afternoon (12:00 PM - 4:00 PM)</option>
                    <option value="Evening (4:00 PM - 6:00 PM)">Evening (4:00 PM - 6:00 PM)</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="consultation-notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  id="consultation-notes"
                  name="notes"
                  rows={4}
                  maxLength={500}
                  value={consultationFormData.notes}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, notes: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent resize-none"
                  placeholder="Tell us about your security needs..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{consultationFormData.notes.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={consultationSubmitting}
                className="w-full bg-[#1a2b4a] text-white px-8 py-3 rounded-lg text-sm font-medium hover:bg-[#2a3b5a] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {consultationSubmitting ? 'Scheduling...' : 'Schedule Consultation'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-12">Visit Our Office</h2>
          <div className="rounded-2xl overflow-hidden shadow-lg h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.1841!2d-73.9875!3d40.7589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQ1JzMyLjAiTiA3M8KwNTknMTUuMCJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
